import { useState, useEffect, useCallback } from "react";
import type { AppSettings, Child, Location, AppPrefs } from "../types";

const DEFAULT_PREFS: AppPrefs = { clockFormat: "12h", tempUnit: "F" };

const DEFAULT_SETTINGS: AppSettings = {
  children: [],
  location: null,
  prefs: DEFAULT_PREFS,
  selectedCalendars: [],
  writeCalendarId: null,
};

export function useSettings() {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/settings")
      .then((r) => r.json())
      .then((raw: unknown) => {
        const data = raw as Partial<AppSettings>;
        setSettings({
          children: data.children ?? [],
          location: data.location ?? null,
          prefs: { ...DEFAULT_PREFS, ...(data.prefs ?? {}) },
          selectedCalendars: data.selectedCalendars ?? [],
          writeCalendarId: data.writeCalendarId ?? null,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const patch = useCallback(async (partial: Partial<AppSettings>) => {
    setSettings((prev) => ({ ...prev, ...partial }));
    await fetch("/api/settings", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(partial),
    });
  }, []);

  const updateChildren = useCallback(
    (children: Child[]) => patch({ children }),
    [patch]
  );

  const updateLocation = useCallback(
    (location: Location | null) => patch({ location }),
    [patch]
  );

  const updatePrefs = useCallback(
    (prefs: AppPrefs) => patch({ prefs }),
    [patch]
  );

  const updateSelectedCalendars = useCallback(
    (selectedCalendars: string[]) => patch({ selectedCalendars }),
    [patch]
  );

  const updateWriteCalendarId = useCallback(
    (writeCalendarId: string | null) => patch({ writeCalendarId }),
    [patch]
  );

  return {
    settings,
    loading,
    updateChildren,
    updateLocation,
    updatePrefs,
    updateSelectedCalendars,
    updateWriteCalendarId,
  };
}
